import {get, filter, some, includes, isEmpty, map, sortBy, find, toUpper, sumBy} from 'lodash'
import {accountTypes, accountSubtypes, clientTypes, whiteLabels} from '@bdswiss/common-enums'
import {config} from '../../config'
import {isWhiteLabel} from '.'
import messages from '../../assets/messages'
import {
  AccountTypeEnumIndices,
  IAccountSubtypeEnum,
} from '@bdswiss/common-enums/lib/esm/enums/account'
import {
  Account,
  AccountSubtype,
  Client,
  ForexAccount,
  PammTauroMarketsManagerAccount,
} from '../../graphql/types'
import {Nullable, UnionToIntersection} from '../../shared/typescript'
import invariant from 'tiny-invariant'
import {overrideFor36376} from './overrides/affiliate_36376'
import {overrideFor36389} from './overrides/affiliate_36389'
import {overrideFor36394} from './overrides/affiliate_36394'
import {overrideFor36602} from './overrides/affiliate_36602'
import {overrideFor36600} from './overrides/affiliate_36600'
import {overrideFor36348And36370} from './overrides/affiliate_36348_36370'
import {overrideFor36439} from './overrides/affiliate_36439'
import {overrideForAffiliates} from './overrides/overrideForAffiliates'
import {overrideFor10119} from './overrides/ib_10119'
import {overrideFor36632} from './overrides/affiliate_36632'
import {overrideFor36621} from './overrides/affiliate_36621'
import {overrideFor36607} from './overrides/affiliate_36607'
import {overrideFor36669} from './overrides/affiliate_36669'
import {overrideFor36667} from './overrides/affiliate_36667'
import {overrideFor10398} from './overrides/ib_10398'
import {overrideFor10337} from './overrides/ib_10337'
import {overrideForKuwaitNetwork} from './overrides/ib_kuwait_network'

export function isDemoAccount({__typename} = {__typename: 0}) {
  return __typename ? accountTypes[__typename].isDemo : false
}

export function isForexAccount({__typename}: {__typename?: string}) {
  if (__typename == null) return false
  return (
    ![
      accountTypes.PAMMAccount.key,
      accountTypes.PAMMSMAccount.key,
      accountTypes.PAMMMauritiusAccount.key,
      accountTypes.PAMMVanuatuAccount.key,
      accountTypes.PAMMSMMauritiusAccount.key,
      accountTypes.PortfolioManagementAccount.key,
      accountTypes.PortfolioManagementMauritiusAccount.key,
      accountTypes.IntroducingBrokerSwissMarketsAccount.key,
      accountTypes.IntroducingBrokerAccount.key,
      accountTypes.IntroducingBrokerSwissMarketsMauritiusAccount.key,
      accountTypes.IntroducingBrokerSwissMarketsVanuatuAccount.key,
      accountTypes.IntroducingBrokerMauritiusAccount.key,
      accountTypes.IntroducingBrokerEquityFlowAccount.key,
      accountTypes.IntroducingBrokerBDSwissVanuatuAccount.key,
      accountTypes.BondAccount.key,
      accountTypes.BondDemoAccount.key,
      accountTypes.BondMauritiusAccount.key,
      accountTypes.BondMauritiusDemoAccount.key,
      accountTypes.BondVanuatuAccount.key,
      accountTypes.BondVanuatuDemoAccount.key,
    ].includes(__typename as AccountTypeEnumIndices) &&
    filter(accountTypes, (a) => includes(['forexMt4', 'forexMt5'], a.category))
      .map((c) => c.key)
      .includes(__typename as AccountTypeEnumIndices)
  )
}
export function getAccountSubtype(subType?: AccountSubtype): IAccountSubtypeEnum {
  const {key} = config
  const whiteLabelObj = find(whiteLabels, (wl) => wl.value === key)
  const subtypes = get(whiteLabelObj, 'supportedSubtypes') || accountSubtypes
  const selectedSubtype = find(
    subtypes,
    (t: IAccountSubtypeEnum) =>
      t.value === toUpper(subType) || t.key === subType || t.label === toUpper(subType),
  )
  return selectedSubtype as unknown as IAccountSubtypeEnum
}

/**
 * Gets the account subtype label with potential overrides based on client's affiliateId
 * For affiliate 10333, "Classic" (basic / mt5std) accounts are displayed as "VIP"
 */
export function getAccountSubtypeLabel(
  accountSubtype: IAccountSubtypeEnum | null | undefined,
  client?: {affiliateId?: number | null} | null,
): string {
  if (!accountSubtype) return ''

  const label = accountSubtype.label || ''
  const subtypeKey = accountSubtype.key?.toLowerCase()

  // For affiliate 10333, show "VIP" instead of "Classic" for classic accounts
  if (
    client?.affiliateId === 10333 &&
    ['basic', 'mt5std'].includes(subtypeKey ?? '')
  ) {
    return 'VIP'
  }

  return label
}
export function isMAMAccount({__typename}) {
  return get(accountTypes, [__typename, 'category']) === 'mam'
}

function getClientMetadataObject(client?: Client | null) {
  if (client?.metadata == null) return undefined
  if (typeof client.metadata === 'object') return client.metadata as any
  try {
    return JSON.parse(client.metadata as string)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Failed to parse client metadata', client?.metadata)
    return undefined
  }
}

const MT5_STD_LIMIT_OVERRIDE_IB_IDS = [10333]
const MT5_STD_LIMIT_OVERRIDE_BY_REMOTE_IB_ID: Record<number, number> = {
  10431: 8,
}

function hasMt5StdLimitOverride(client?: Client | null, accounts?: any[]): boolean {
  if (!client) return false

  // Check if client has an IB account with remoteId = 10333
  if (accounts && Array.isArray(accounts)) {
    const ibAccounts = filter(accounts, (account) => isIntroducingBrokerAccount(account))
    const hasIbAccountWithRemoteId = some(ibAccounts, (account) => {
      const remoteId = account.remoteId != null ? Number(account.remoteId) : undefined
      return remoteId !== undefined && MT5_STD_LIMIT_OVERRIDE_IB_IDS.includes(remoteId)
    })
    if (hasIbAccountWithRemoteId) {
      return true
    }
  }

  // Check client.affiliateId
  const affiliateId = client.affiliateId != null ? Number(client.affiliateId) : undefined
  if (affiliateId !== undefined && MT5_STD_LIMIT_OVERRIDE_IB_IDS.includes(affiliateId)) {
    return true
  }

  // Check remoteIbId from metadata (for followers)
  const metadataObj = getClientMetadataObject(client)
  const remoteIbId = metadataObj?.ibFollowerData?.remoteIbId
  if (remoteIbId != null && MT5_STD_LIMIT_OVERRIDE_IB_IDS.includes(Number(remoteIbId))) {
    return true
  }

  return false
}

function getMt5StdLimitOverrideByRemoteIbId(client?: Client | null): number | undefined {
  const metadataObj = getClientMetadataObject(client)
  const remoteIbId = metadataObj?.ibFollowerData?.remoteIbId
  if (remoteIbId == null) return undefined

  return MT5_STD_LIMIT_OVERRIDE_BY_REMOTE_IB_ID[Number(remoteIbId)]
}

function getAdjustedMaxAllowed(
  subType: IAccountSubtypeEnum,
  client: Client,
  accounts?: any[],
): number | undefined {
  const subtypeKey = subType?.key?.toLowerCase()
  if (subtypeKey === 'mt5std') {
    const remoteIbFollowerLimit = getMt5StdLimitOverrideByRemoteIbId(client)
    if (remoteIbFollowerLimit != null) {
      return remoteIbFollowerLimit
    }
    if (hasMt5StdLimitOverride(client, accounts)) {
      return 50
    }
  }
  return subType?.maxAllowed
}

export function isMAMMasterAccount({__typename, accountSubtype}) {
  return (
    get(accountTypes, [__typename, 'category']) === 'mam' &&
    accountSubtype === accountSubtypes.master.key
  )
}

export function isMAMScSubAccount({
  __typename,
  accountSubtype,
}: {
  __typename?: Nullable<string>
  accountSubtype?: Nullable<string>
}): boolean {
  if (accountSubtype == null) return false

  return (
    get(accountTypes, [__typename ?? '', 'category']) === 'mam' &&
    accountSubtype === accountSubtypes.sc_sub.key
  )
}

export function isPremiumAccount({__typename}) {
  return includes(
    [accountTypes.ForexPremiumMauritiusAccount.key, accountTypes.ForexPremiumVanuatuAccount.key],
    __typename,
  )
}

export function isPAMMFundManagerAccount({__typename}: {__typename?: string}) {
  if (__typename == null) return false

  return [
    accountTypes.PAMMFundManagerAccount.key,
    accountTypes.PAMMSMFundManagerAccount.key,
    accountTypes.PAMMFundManagerMauritiusAccount.key,
    accountTypes.PAMMSMFundManagerMauritiusAccount.key,
    accountTypes.PAMMSMFundManagerVanuatuAccount.key,
    accountTypes.PAMMFundManagerVanuatuAccount.key,
  ].includes(__typename as AccountTypeEnumIndices)
}

export function isPortfolioManagementAccount({__typename}) {
  return [
    accountTypes.PortfolioManagementAccount.key,
    accountTypes.PortfolioManagementMauritiusAccount.key,
  ].includes(__typename)
}

export function isPAMMCysecProduct({__typename}) {
  return [accountTypes.PAMMAccount.key, accountTypes.PAMMSMAccount.key].includes(__typename)
}

// TODO: This needs to be examined, we dont have tauromarkets in there! Also, any new whitelabel will be missing as well
export function isAffiliatesAccount({__typename}: {__typename?: string}) {
  if (__typename == null) return false
  return [
    accountTypes.AffiliateAccount.key,
    accountTypes.AffiliateVanuatuAccount.key,
    accountTypes.AffiliateMauritiusAccount.key,
  ].includes(__typename as AccountTypeEnumIndices)
}

export function isIntroducingBrokerAccount({__typename}: {__typename?: string}): boolean {
  if (__typename == null) return false
  return [
    accountTypes.IntroducingBrokerAccount.key,
    accountTypes.IntroducingBrokerTauroMarketsAccount.key,
    accountTypes.IntroducingBrokerTauroMarketsMauritiusAccount.key,
    accountTypes.IntroducingBrokerSwissMarketsAccount.key,
    accountTypes.IntroducingBrokerMauritiusAccount.key,
    accountTypes.IntroducingBrokerEquityFlowAccount.key,
    accountTypes.IntroducingBrokerSwissMarketsMauritiusAccount.key,
    accountTypes.IntroducingBrokerSwissMarketsVanuatuAccount.key,
    accountTypes.IntroducingBrokerBDSwissVanuatuAccount.key,
  ].includes(__typename as AccountTypeEnumIndices)
}

export function isTradesmarterAccount({__typename}: Account): boolean {
  if (__typename == null) return false

  return filter(accountTypes, {category: 'tradeSmarter'})
    .map((c) => c.key)
    .includes(__typename as AccountTypeEnumIndices)
}

export function isBitnukAccount(account: {__typename?: string} | undefined): boolean {
  if (!account || account.__typename == null) return false
  return filter(accountTypes, {subCategory: 'bitnuk'})
    .map((c) => c.key)
    .includes(account.__typename as AccountTypeEnumIndices)
}
export function isKartwheelAccount(account?: Account): boolean {
  if (account?.__typename == null) return false

  const accountType = accountTypes[account.__typename]
  if (accountType.category === 'kartwheel') {
    return true
  }
  return false
}

export function isPammManagerAccount(account?: Account): account is PammTauroMarketsManagerAccount {
  if (account?.__typename == null) return false

  const accountType = accountTypes[account.__typename]
  return (
    accountType.productId === accountTypes.PAMMTauroMarketsManagerAccount.productId ||
    accountType.productId === accountTypes.PAMMTauroMarketsMauritiusManagerAccount.productId ||
    accountType.productId === accountTypes.PAMMTauroMarketsMT5ManagerAccount.productId
  )
}

export function isPammManagerTradeAccount(account?: Account) {
  if (!account) return false
  if (!account.__typename) return false
  if (!account.metadata) return false

  const parsedMetadata = JSON.parse(account.metadata)
  const hasPammTradesAccountKey = 'pammTradesAccount' in parsedMetadata
  if (!hasPammTradesAccountKey) return false

  //TODO: Update logic to determine PAMM Manager trade account and PAMM Investor trade account
  // Have big pitfall - PAMM Manager trade account and PAMM Investor trade account both have 'pammTradesAccount' key in metadata, but
  // manager account has ID of PAMM account as value and for Investor it's boolean value ¯\_(ツ)_/¯
  return typeof parsedMetadata.pammTradesAccount === 'number'
}

export function isPammInvestorTradeAccount(account?: Account) {
  if (!account) return false
  if (!account.__typename) return false
  if (!account.metadata) return false

  const parsedMetadata = JSON.parse(account.metadata)
  const hasPammTradesAccountKey = 'pammTradesAccount' in parsedMetadata
  if (!hasPammTradesAccountKey) return false

  //TODO: Update logic to determine PAMM Manager trade account and PAMM Investor trade account
  // Have big pitfall - PAMM Manager trade account and PAMM Investor trade account both have 'pammTradesAccount' key in metadata, but
  // manager account has ID of PAMM account as value and for Investor it's boolean value ¯\_(ツ)_/¯
  return typeof parsedMetadata.pammTradesAccount === 'boolean'
}

export function isPammManagerFeesAccount(account?: Account) {
  if (!account) return false
  if (!account.__typename) return false
  if (!account.metadata) return false

  const parsedMetadata = JSON.parse(account.metadata)
  return !!parsedMetadata.pammFeesAccount
}

export function isPammInvestorAccount(account: {__typename: AccountTypeEnumIndices}) {
  const accountType = accountTypes[account.__typename]
  return (
    accountType.productId === accountTypes.PAMMTauroMarketsAccount.productId ||
    accountType.productId === accountTypes.PAMMTauroMarketsMauritiusAccount.productId ||
    accountType.productId === accountTypes.PAMMTauroMarketsMT5Account.productId
  )
}

/** IANA zone for CET/CEST — must match bdswiss-graphql `common/isCetWeekend`. */
const CET_TIMEZONE = 'Europe/Berlin'

/** Saturday & Sunday in CET/CEST (Europe/Berlin; DST handled automatically). */
export function isCetWeekend(date: Date = new Date()): boolean {
  const weekday = new Intl.DateTimeFormat('en-US', {timeZone: CET_TIMEZONE, weekday: 'long'}).format(
    date,
  )
  return weekday === 'Saturday' || weekday === 'Sunday'
}

/** PAMM Money Manager or Investor account during CET weekend — withdrawals are blocked. */
export function isPammWithdrawalBlockedOnWeekend(account?: Account): boolean {
  if (!account?.__typename) return false
  return (
    isCetWeekend() &&
    (isPammInvestorAccount(account as {__typename: AccountTypeEnumIndices}) ||
      isPammManagerAccount(account))
  )
}
export function blockedDepositAccount(account: ForexAccount): boolean {
  const {isViewOnly, isArchived, __typename} = account
  if (
    isViewOnly ||
    isArchived ||
    isBrokereePerformanceFeeAccount(account) ||
    isPammManagerTradeAccount(account) ||
    isPammInvestorTradeAccount(account)
  ) {
    return true
  }
  invariant(__typename, 'Missing __typename in account')
  return includes(
    filter(accountTypes, {clientDepositPermitted: false, isDemo: false}).map((c) => c.key),
    __typename as AccountTypeEnumIndices,
  )
}

export function isBrokereePerformanceFeeAccount(account: ForexAccount): boolean {
  const metadata = getMetadataFromAccount(account)
  return metadata.brokereeProviderAccountId != null
}

export function blockedWithdrawalAccount(account: ForexAccount): boolean {
  const {__typename} = account
  if (isPammManagerTradeAccount(account)) {
    return true
  }

  invariant(__typename, 'Missing __typename in account')
  return includes(
    filter(accountTypes, {clientWithdrawalPermitted: false, isDemo: false}).map((c) => c.key),
    __typename as AccountTypeEnumIndices,
  )
}

export function isWalletAccountClientType(clientType) {
  return [
    clientTypes.affiliate.value,
    clientTypes.affiliateCorporate.value,
    clientTypes.ib.value,
    clientTypes.ibCorporate.value,
  ].includes(clientType)
}

export function hasOnlyDepositBlockedAccounts(accounts) {
  const otherAccounts = filter(accounts, (__typename) => !blockedDepositAccount(__typename))
  return accounts && otherAccounts.length === 0
}

export function hasAffiliateAccount(accounts) {
  const affiliateAccount = filter(accounts, (__typename) => isAffiliatesAccount(__typename))
  return accounts && affiliateAccount.length > 0
}

export function hasIntroducingBrokerAccount(accounts) {
  const ibAccount = filter(accounts, (__typename) => isIntroducingBrokerAccount(__typename))
  return accounts && ibAccount.length > 0
}

export function isMt5ForexAccount(__typename) {
  return get(accountTypes, [__typename, 'category']) === 'forexMt5'
}

export function isForexBdxAccount({__typename}) {
  return filter(accountTypes, {subCategory: 'bdx'})
    .map((c) => c.key)
    .includes(__typename)
}

export function isSMPrimeWalletAccount({__typename}) {
  return filter(accountTypes, {category: 'smPrime'})
    .map((c) => c.key)
    .includes(__typename)
}

export function isEmptySubscriptionAccount(account) {
  return includes(
    filter(accountTypes, {subscription: true}).map((c) => c.key),
    account.__typename,
  )
    ? isEmpty(account.subscription)
    : false
}

export function isAffiliateClient(clientType) {
  return [clientTypes.affiliate.value, clientTypes.affiliateCorporate.value].includes(clientType)
}

export function isIBClient(clientType) {
  return [clientTypes.ib.value, clientTypes.ibCorporate.value].includes(clientType)
}

export function hasWalletProductAccount(accounts) {
  const walletProductAccount = filter(accounts, (a) =>
    get(accountTypes[a.__typename], 'walletProduct'),
  )
  return accounts && walletProductAccount.length > 0
}

export function hasVipOrRaw(accounts, demo?) {
  return some(
    accounts,
    (a) =>
      (demo ? a.isDemo : !a.isDemo) &&
      includes([accountSubtypes.black.key, accountSubtypes.raw.key], a.accountSubtype),
  )
}

export function isVipOrRaw(account, type?, demo?) {
  return (
    includes(
      [accountSubtypes.black.key, accountSubtypes.raw.key],
      get(account, 'accountSubtype'),
    ) ||
    (type &&
      (!demo || (demo && type.isDemo)) &&
      includes([accountSubtypes.black.key, accountSubtypes.raw.key], get(account, 'key')))
  )
}

export function hasVipAccount(accounts) {
  return some(accounts, (a) => includes([accountSubtypes.black.key], a.accountSubtype))
}

export function hasOnlyWalletProductAccount(accounts) {
  const walletProductAccount = filter(accounts, (a) =>
    get(accountTypes[a.__typename], 'walletProduct'),
  )
  return accounts && walletProductAccount.length === accounts.length
}

export function isCentAccount(account?: {__typename?: string}): boolean {
  return account?.__typename
    ? includes(
        [accountTypes.CentMauritiusAccount.key, accountTypes.CentVanuatuAccount.key],
        account.__typename,
      )
    : false
}

const overrides = [
  overrideFor36376,
  overrideFor36389,
  overrideFor36394,
  overrideFor36348And36370,
  overrideFor36439,
  overrideFor36602,
  overrideFor36600,
  overrideFor10119,
  overrideFor10398,
  overrideFor36632,
  overrideFor36621,
  overrideFor36607,
  overrideFor36669,
  overrideFor36667,
  overrideForAffiliates,
  overrideFor10337,
  overrideForKuwaitNetwork,
]

export function getAllowedAccounts(accounts, companyObject, t, allowedAccountTypes, client) {
  const {key} = config
  if (!accounts) return []

  const whiteLabel = isWhiteLabel()
  const whiteLabelSupportedSubtypes: any =
    whiteLabel && map(get(whiteLabels[key], 'supportedSubtypes'), 'value')
  const country = toUpper(get(client, 'address.country'))
  let isRestrictedOrFollower
  let allowedAccount = filter(accountTypes, (a: any) => {
    if (a.company !== companyObject.key) {
      return false
    }
    if (a.deprecated) {
      return false
    }
    // TODO: temp hide demo account from the list SKYG-753
    if (a.isDemo && config.visual?.accounts?.hideDemoAccountSelection) {
      return false
    }
    if (!a.clientOpenPermitted) {
      return false
    }

    // https://skyground.atlassian.net/browse/SKYG-2141
    if (
      a.productId === accountTypes.PAMMTauroMarketsManagerAccount.productId ||
      a.productId === accountTypes.PAMMTauroMarketsMauritiusManagerAccount.productId ||
      a.productId === accountTypes.PAMMTauroMarketsMT5ManagerAccount.productId
    ) {
      return [
        clientTypes.ib.value,
        clientTypes.ibCorporate.value,
        clientTypes.corporate.value,
        clientTypes.affiliate.value,
        clientTypes.affiliateCorporate.value,
        clientTypes.hybrid.value,
      ].includes(client.clientType)
    }

    const isBaseProduct =
      whiteLabel && whiteLabels[key].baseProducts
        ? includes(whiteLabels[key].baseProducts, a.key)
        : true
    if (!isBaseProduct) {
      return false
    }

    const notInDisallowedCountries =
      !a.disallowedCountries ||
      (a.disallowedCountries &&
        (a.manuallyAllowed
          ? (a.clientPermittedByCountry && !includes(a.disallowedCountries, country)) ||
            includes(client.manuallyAllowedAccounts, a.key)
          : !includes(a.disallowedCountries, country)))
    if (!notInDisallowedCountries) {
      return false
    }

    const clientAllowedToCreateMoreAccounts =
      !a.clientMaxAllowed ||
      (a.clientMaxAllowed &&
        // @ts-ignore
        sumBy(accounts, (account) => account.__typename === a.key) < a.clientMaxAllowed)
    if (!clientAllowedToCreateMoreAccounts) {
      return false
    }

    const restrictedAccountTypes = config.restrictedAccountTypes
    const accountType: AccountTypeEnumIndices = a.key
    if (restrictedAccountTypes && restrictedAccountTypes[accountType]) {
      const restrictions = restrictedAccountTypes[accountType]
      const isIncludedClient = restrictions.clientIds.includes(client.id)
      if (restrictions.includeAffiliate && isIncludedClient) {
        isRestrictedOrFollower = true
        return true
      }
      if (!restrictions.includeAffiliateFollowers) {
        return false
      }
      const affiliateIds: number[] = client.registrationCampaigns?.map((x) => x.affiliateId) ?? []
      for (const id of affiliateIds) {
        if (restrictions.clientIds.includes(id)) {
          isRestrictedOrFollower = true
          return isRestrictedOrFollower
        }
      }
      // Client is not in allow list and not under an allowed affiliate — do not show this restricted account type (e.g. MT4 on MAU)
      return a.company === 'tauroMarkets'
    }

    // TODO: Remove after testing
    if (
      a.productId === accountTypes.ForexMT5TauromarketsDemoAccount.productId
    ) {
      const params = new URLSearchParams(window.location.search)
      return !!params.get('mauMt5Demo')
    }
    return true
  })

  // Modify allowedAccountsList for restricted clients and followers
  if (isRestrictedOrFollower) {
    // IB followers who should see IB account in MT4 tab (even though other MT4 accounts are restricted)
    const ibFollowersWithIbInMt4 = [10333, 10350, 10354, 10382, 10389, 10390, 10391, 10392, 10400, 10402, 10404, 10407, 10408, 10409, 10416, 10417, 10414, 10443, 10442, 10439]
    const shouldShowIbInMt4 = ibFollowersWithIbInMt4.includes(client.affiliateId ?? 0)

    const excludedAccounts = [
      'AffiliateTauroMarketsAccount',
      // Allow IntroducingBrokerTauroMarketsAccount for specific IB followers who should see it in MT4 tab
      ...(shouldShowIbInMt4 ? [] : ['IntroducingBrokerTauroMarketsAccount']),
      'ForexClassicTauroMarketsDemoAccount',
      'ForexRawTauroMarketsAccount',
      'AffiliateTauroMarketsMauritiusAccount',
      // IntroducingBrokerTauroMarketsMauritiusAccount should remain available under MT5 tab
      // even when MT4 tab is hidden for certain IB followers
      // 'IntroducingBrokerTauroMarketsMauritiusAccount',
    ]

    allowedAccount = allowedAccount.filter((account) => !excludedAccounts.includes(account.key))
    allowedAccount.forEach((account) => {
      if (account.supportedSubtypes) {
        account.supportedSubtypes = account.supportedSubtypes.filter(
          (subType) => subType === 'MT5_STD' || subType === 'BASIC',
        )
      }
    })
  }

  // Display only TauroMarkets IB accounts
  allowedAccount = allowedAccount.filter(
    (account) =>
      [
        'IntroducingBrokerTauroMarketsAccount',
        'IntroducingBrokerTauroMarketsMauritiusAccount',
      ].includes(account.key) || !account.key.includes('IntroducingBroker'),
  )

  const allowedAccountsList: any[] = []
  map(allowedAccount, (type: any) => {
    const baseProduct = type.baseProduct && type.baseProduct.replace(/^\w/, (c) => c.toLowerCase())
    if (type.supportedSubtypes && !type.subscription) {
      map(type.supportedSubtypes, (subType) => {
        if (
          !isEmpty(whiteLabelSupportedSubtypes) &&
          !includes(whiteLabelSupportedSubtypes, subType)
        ) {
          return
        }
        const selectedSubType: any = getAccountSubtype(subType)
        if (
          !isEmpty(allowedAccountTypes) &&
          !includes(allowedAccountTypes, selectedSubType.key) &&
          !type.isDemo
        ) {
          return
        }
        if (type.isDemo && selectedSubType.clientOpenPermittedDemo === false) {
          return
        }
        const clientAccountsLimitCount = get(
          filter(
            accounts,
            (a) => {
              const meta = getMetadataFromAccount(a)

              const isAccountDemo = a.isDemo !== undefined ? a.isDemo : isDemoAccount(a)
              // For demo accounts, also check if the account type matches the demo account type
              const isCorrectDemoType = type.isDemo ?
                (isAccountDemo && (a.accountSubtype === selectedSubType.key || a.__typename === type.key)) :
                (!isAccountDemo && a.accountSubtype === selectedSubType.key)
              return isCorrectDemoType &&
                !a.isViewOnly &&
                !a.isArchived &&
                !meta.brokereeProviderAccountId // we skip accounts that are created as BrokereePerformanceFee [PT-3752]
          }),
          'length',
        )
        const adjustedMaxAllowed = getAdjustedMaxAllowed(selectedSubType, client, accounts)
        const adjustedLimitValue =
          typeof adjustedMaxAllowed === 'number' ? adjustedMaxAllowed : undefined
        const remainingSlots =
          adjustedLimitValue != null ? adjustedLimitValue - clientAccountsLimitCount : undefined
        const canCreateMore = adjustedLimitValue == null || (remainingSlots ?? 0) > 0
        const subTypeAlreadyExists = find(
          allowedAccountsList,
          (a) =>
            a.accountSubtype === selectedSubType.key &&
            (!selectedSubType.allowDemo || (selectedSubType.allowDemo && a.isDemo)) &&
            a.isDemo === type.isDemo,
        )
        if (subTypeAlreadyExists) {
          subTypeAlreadyExists.accountTypesList &&
            subTypeAlreadyExists.accountTypesList.push(type.key)
        } else {
          if (
            !selectedSubType.hideFromClients &&
            canCreateMore
          ) {
            const accountSubTypeObject = {
              label: `${selectedSubType.label}  ${t(messages.account.i18nKey)}`,
              icon: get(selectedSubType, 'shortLabel') || type.shortLabel,
              accountSubtype: selectedSubType.key,
              accountType: type.key,
              accountTypesList: [type.key],
              category: type.category,
              subCategory: type.subCategory || '',
              baseProduct,
              walletProduct: type.walletProduct,
              maxAllowed: adjustedLimitValue,
              available: adjustedLimitValue != null ? remainingSlots : '',
              isDemo: type.isDemo,
              showDemoLabel: type.showDemoLabel,
            }
            allowedAccountsList.push(accountSubTypeObject)
          }
        }
      })
    } else {
      const clientAccountsLimitCount = get(
        filter(accounts, (a) => a.__typename === type.key && !type.supportsMultipleAccounts),
        'length',
      )
      if (clientAccountsLimitCount === 0 || !clientAccountsLimitCount) {
        const account = {
          label: `${type.label}`,
          icon: type.shortLabel,
          accountType: type.key,
          category: type.category,
          subCategory: type.subCategory || '',
          baseProduct,
          walletProduct: type.walletProduct,
          isDemo: type.isDemo,
        }
        allowedAccountsList.push(account)
      }
    }
  })
  const result = sortBy(allowedAccountsList, (a) => !!a.walletProduct)
  return overrides.reduce((newResults, override) => override(newResults, client), result)
}

export function getForcedServerName(account, Images?) {
  const forcedServerNameDetails = config.serverNameForced
  let forcedServerName = null
  let forcedServerExampleImage = null
  if (forcedServerNameDetails) {
    // const validDetails =  ((moment(account.createdAt) < moment(forcedServerNameDetails.date))
    // && (moment() < moment(forcedServerNameDetails.validUntil)))
    forcedServerName = get(forcedServerNameDetails.serverName, account.server)
    forcedServerExampleImage = get(
      Images,
      get(forcedServerNameDetails.exampleImages, account.server),
    )
  }
  return {forcedServerName, forcedServerExampleImage}
}

export const isViewOnlyAccount = (account: Account): boolean =>
  'isViewOnly' in account && Boolean(account.isViewOnly)

export const isApprovedAccount = (account: Account): boolean =>
  'approved' in account && Boolean(account.approved)

export const getMetadataFromAccount = (account: Account): Record<string, unknown> => {
  try {
    return 'metadata' in account ? JSON.parse(account.metadata as string) : {}
  } catch (e) {
    return {}
  }
}

export function getAccountLabel(account: Account, locale: string): string {
  const {hasAnotherCurrenciesSymbols} = config
  const providerAccount = account.connectedBrokereeProviderAccount

  if (providerAccount != null) {
    return `Performance Fees Account (Provider: ${providerAccount.remoteId})`
  }

  const accountType =
    accountTypes[account.__typename ?? ''] ||
    ('type' in account ? accountTypes[account.type as string] : null)

  const tradeSmarter = isViewOnlyAccount(account) && isTradesmarterAccount(account) && 'Wallet'
  if (!hasAnotherCurrenciesSymbols) {
    return (
      tradeSmarter ||
      get(account, 'accountName') ||
      accountType.localization.t(locale) ||
      accountType.label
    )
  }

  const label =
    tradeSmarter ||
    get(account, 'accountName') ||
    accountType.localization.t(locale) ||
    accountType.label
  return label.replace(/USD/g, 'GUSD').replace(/EUR/g, 'GEUR')
}

export function hasBinaryProductAccount(accounts) {
  const binaryProductAccount = filter(
    accounts,
    (a) => get(accountTypes[a.__typename], 'category') === 'tradeSmarter',
  )
  return accounts && binaryProductAccount.length > 0
}

export function checkAccountCopyTrading(account) {
  const {
    featuresConfig: {copyTradingEnabled},
  } = config
  const accountTypeName = get(account, '__typename')
  const accountType = accountTypeName && accountTypes[account.__typename]
  const accountSubtype = get(account, 'accountSubtype')
  return (
    includes(get(copyTradingEnabled, 'accountSubTypes', []), accountSubtype) &&
    includes(get(copyTradingEnabled, 'accountTypesCategory', []), accountType.category) &&
    !account.isDemo &&
    !account.isViewOnly &&
    !account.isArchived &&
    !isPammManagerTradeAccount(account) &&
    !isPammManagerFeesAccount(account)
  )
}

export function isAccountFitsBrokereeCopyTradingConfig(account: Account): boolean {
  const {
    featuresConfig: {brokereeCopyTradingEnabled},
  } = config
  const accountProductId = accountTypes[account.__typename ?? '']?.productId as number | undefined
  if (accountProductId == null) return false

  const availableProductIds = Object.keys(brokereeCopyTradingEnabled ?? {})
  const hasCorrectProduct =
    accountProductId != null && availableProductIds.includes(String(accountProductId))

  const availableSubtypes = brokereeCopyTradingEnabled?.[accountProductId] ?? []
  const accountSubtype = get(account, 'accountSubtype') as string | undefined
  const hasCorrectSubtype = accountSubtype != null && availableSubtypes.includes(accountSubtype)

  return hasCorrectProduct && hasCorrectSubtype
}

export function canBeBrokereeCopyTradingProvider(account: Account, client: Client): boolean {
  const isValidClient =
    (Boolean(client.clientAllowCopyTrading) || Boolean(client.isParticipantOfSocialTrading)) &&
    client.id != null

  const isValidAccount =
    isAccountFitsBrokereeCopyTradingConfig(account) &&
    //@ts-ignore union type missmatch
    !account.isSubscribedToBrokereeOffer &&
    !account.hidden &&
    //@ts-ignore union type missmatch
    !account.isArchived &&
    //@ts-ignore union type missmatch
    !account.provideCopyTrading // make sure that old copy-trading is disabled

  return isValidClient && isValidAccount
}

export function canBeBrokereeCopyTradingFollower(account: Account, client: Client): boolean {
  return (
    client.id != null &&
    isAccountFitsBrokereeCopyTradingConfig(account) &&
    //@ts-ignore union type missmatch
    !account.isProvidingBrokereeOffer &&
    !account.hidden &&
    //@ts-ignore union type missmatch
    !account.isArchived
  )
}

export function isWalletAccount(__typename) {
  return !!get(accountTypes[__typename], 'walletProduct')
}

export function hasValidAccountSubTypes(
  accounts,
  accountSubTypes,
  notReadOnly = false,
  nodeDemo = null,
) {
  return some(
    accounts,
    (a) =>
      includes(accountSubTypes, a.accountSubtype) &&
      (!notReadOnly || (notReadOnly && !a.isReadOnly)) &&
      (!nodeDemo || (nodeDemo && !a.isDemo)),
  )
}

export function getAccountTypeForStats(account) {
  if (account.category === 'cent') return 'cent'
  return account.accountSubtype
}

export const isConnectedPammInvestorAccount = (account) =>
  account.__typename === accountTypes.PAMMTauroMarketsAccount.key && !account.unconnected

export const getAccountProperty = <T extends Account, K extends keyof UnionToIntersection<T>>(
  obj: T,
  key: K,
): K extends keyof T ? T[K] : UnionToIntersection<T>[K] | undefined =>
  // @ts-ignore
  key in obj ? obj[key as string] : undefined

export function getAllowedAccountsByCampaignRules(accounts, campaign) {
  if (!campaign) return accounts

  const {accountTypesVisibilityRules} = campaign
  const {
    isMt4ClassicVisible,
    isMt4VipVisible,
    isMt4RawVisible,
    isMt5ClassicVisible,
    isMt5VipVisible,
    isMt5RawVisible,
    isIbWalletVisible,
    isAffiliateAccountVisible,
    isMt4DemoAccountVisible,
    isMt5DemoAccountVisible,
  } = accountTypesVisibilityRules

  return accounts.filter(account => {
    if (isIntroducingBrokerAccount({__typename: account.accountType}) && isIbWalletVisible === false) {
      return false
    }
    if (account.category === 'affiliate' && isAffiliateAccountVisible === false) {
      return false
    }
    if (account.isDemo) {
      if (account.category === 'forexMt4' && isMt4DemoAccountVisible === false) {
        return false
      }
      if (account.category === 'forexMt5' && isMt5DemoAccountVisible === false) {
        return false
      }
    }

    const isDemo = account.isDemo

    const isMt4 = account.category === 'forexMt4'
    const isMt5 = account.category === 'forexMt5'

    const isClassic = account.accountSubtype === 'basic' || account.accountSubtype === 'mt5Std'
    const isRaw = account.accountSubtype === 'raw' || account.accountSubtype === 'mt5Raw'
    const isVip = account.accountSubtype === 'black' || account.accountSubtype === 'mt5Vip'

    if (isMt4 && isClassic && !isDemo && !isMt4ClassicVisible) return false
    if (isMt4 && isRaw && !isMt4RawVisible) return false
    if (isMt4 && isVip && !isMt4VipVisible) return false

    if (isMt5 && isClassic && !isMt5ClassicVisible) return false
    if (isMt5 && isRaw && !isMt5RawVisible) return false
    if (isMt5 && isVip && !isMt5VipVisible) return false

    return true
  })
}
